import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Target, Eye, Award, ArrowRight } from 'lucide-react';

const NosotrosV2 = () => {
  const valores = [
    { icon: Award, title: 'Calidad', desc: 'Productos de la más alta calidad con los mejores proveedores del mercado' },
    { icon: Target, title: 'Compromiso', desc: 'Comprometidos con la satisfacción total de nuestros clientes' },
    { icon: CheckCircle, title: 'Innovación', desc: 'Constante actualización en tecnología y procesos de transformación' },
    { icon: Eye, title: 'Transparencia', desc: 'Relaciones comerciales basadas en la honestidad y confianza' },
  ];

  const timeline = [
    { year: '2007', title: 'Fundación', desc: 'Nace NAYACEROS en Tepic, Nayarit' },
    { year: '2010', title: 'Primera Expansión', desc: 'Apertura de sucursal Independencia' },
    { year: '2015', title: 'Centro de Transformación', desc: 'Inauguración con maquinaria CNC' },
    { year: '2018', title: 'Sucursal Acaponeta', desc: 'Expansión hacia el norte de Nayarit' },
    { year: '2024', title: 'Actualidad', desc: '4 sucursales y más de 17 años' },
  ];

  return (
    <div className="bg-v2-darker min-h-screen">
      {/* Hero Banner */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=1920"
            alt="Nosotros"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-0.5 bg-v2-orange" />
            <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Quiénes Somos</span>
            <span className="w-12 h-0.5 bg-v2-orange" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Sobre <span className="text-v2-orange">Nosotros</span>
          </h1>
        </div>
      </div>

      {/* About Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-4 border-l-4 border-v2-orange" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-4 border-r-4 border-v2-orange" />
              <img 
                src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800"
                alt="NAYACEROS"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-8 left-8 bg-v2-orange px-8 py-6">
                <span className="text-4xl font-bold text-white block">2007</span>
                <span className="text-white/80 text-sm uppercase tracking-wider">Fundación</span>
              </div>
            </div>

            <div>
              <p className="text-v2-text-muted text-lg leading-relaxed mb-6">
                <strong className="text-white">NAYACEROS</strong> nace en el año 2007 y es una empresa dedicada a la 
                transformación, fabricación y distribución de aceros en todas sus formas, medidas y espesores; 
                aceros especiales, laminados, forjados, doblados y muchos más.
              </p>
              
              <p className="text-v2-text-muted leading-relaxed mb-6">
                Nos caracterizamos por ser una empresa joven, con empuje y experiencia en el rubro de aceros 
                para la industria metalúrgica, apostando siempre al crecimiento y constante innovación.
              </p>
              
              <p className="text-v2-text-muted leading-relaxed mb-8">
                Actualmente NAYACEROS se ha adaptado a las crecientes demandas del mercado, ofreciendo a 
                nuestros clientes asesoramiento en cuanto a las especificaciones técnicas de los materiales.
              </p>

              <Link 
                to="/contacto" 
                className="group inline-flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
                style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}
              >
                Contáctanos
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-v2-dark">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-v2-gray border border-v2-border p-10 group hover:border-v2-orange transition-colors">
              <div className="w-16 h-16 bg-v2-orange/10 group-hover:bg-v2-orange rounded-lg flex items-center justify-center mb-6 transition-colors">
                <Target className="text-v2-orange group-hover:text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white uppercase mb-4">Misión</h3>
              <p className="text-v2-text-muted leading-relaxed">
                Proveer soluciones integrales en acero a la industria metalúrgica, automotriz, petrolera, 
                gasífera y agroindustrial, ofreciendo productos de calidad y servicio personalizado.
              </p>
            </div>
            
            <div className="bg-v2-gray border border-v2-border p-10 group hover:border-v2-orange transition-colors">
              <div className="w-16 h-16 bg-v2-orange/10 group-hover:bg-v2-orange rounded-lg flex items-center justify-center mb-6 transition-colors">
                <Eye className="text-v2-orange group-hover:text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white uppercase mb-4">Visión</h3>
              <p className="text-v2-text-muted leading-relaxed">
                Ser la empresa líder en transformación y distribución de aceros en el occidente de México, 
                reconocida por nuestra innovación tecnológica y calidad de servicio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-12 h-0.5 bg-v2-orange" />
              <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Principios</span>
              <span className="w-12 h-0.5 bg-v2-orange" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Nuestros <span className="text-v2-orange">Valores</span>
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {valores.map((valor, idx) => (
              <div key={idx} className="text-center group">
                <div className="w-24 h-24 bg-v2-gray border border-v2-border group-hover:border-v2-orange group-hover:bg-v2-orange rounded-full flex items-center justify-center mx-auto mb-6 transition-all">
                  <valor.icon className="text-v2-orange group-hover:text-white" size={36} />
                </div>
                <h3 className="text-xl font-bold text-white uppercase mb-2">{valor.title}</h3>
                <p className="text-v2-text-muted text-sm">{valor.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-v2-dark">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-12 h-0.5 bg-v2-orange" />
              <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Trayectoria</span>
              <span className="w-12 h-0.5 bg-v2-orange" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Nuestra <span className="text-v2-orange">Historia</span>
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-v2-border" />
            
            {timeline.map((item, idx) => (
              <div key={idx} className={`relative flex items-center mb-12 ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                <div className={`flex-1 ${idx % 2 === 0 ? 'md:pl-12 md:text-right' : 'md:pr-12'} pl-12 md:pl-0`}>
                  <div className="bg-v2-gray border border-v2-border p-6 hover:border-v2-orange transition-colors">
                    <span className="text-v2-orange text-2xl font-bold">{item.year}</span>
                    <h4 className="text-white font-bold uppercase mt-1">{item.title}</h4>
                    <p className="text-v2-text-muted text-sm mt-1">{item.desc}</p>
                  </div>
                </div>
                <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-v2-orange rounded-full transform md:-translate-x-1/2 ring-4 ring-v2-darker" />
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NosotrosV2;
